import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../common/Button";
import { LanguageSwitcher } from "../../common/LanguageSwitcher";
import { bugsnagClient } from "../../../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../../../utils/analytics";
import api from "../../../utils/api";
import type {
  InviteValidationResponse,
  RegisterFormData,
  RegisterPhase,
  RegisterResponse,
} from "./types";

const SUPPORT_EMAIL = "contact@monco.ai";
const MINIMUM_PASSWORD_LENGTH = 6;
const REDIRECT_DELAY_MILLISECONDS = 1500;

const INITIAL_FORM_DATA: RegisterFormData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  invitationToken: "",
};

export const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ token: string }>();

  const [formData, setFormData] = useState<RegisterFormData>(INITIAL_FORM_DATA);
  const [phase, setPhase] = useState<RegisterPhase>({ phase: "checking" });

  const invite = "invite" in phase ? phase.invite : null;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    setPhase((currentPhase) =>
      currentPhase.phase === "form"
        ? { ...currentPhase, submitError: null }
        : currentPhase,
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (phase.phase !== "form") {
      return;
    }

    const { invite: submittedInvite } = phase;
    const { confirmPassword, ...registerPayload } = formData;

    if (formData.password.length < MINIMUM_PASSWORD_LENGTH) {
      setPhase({
        phase: "form",
        invite: submittedInvite,
        submitError: t("profile.password.min_length"),
      });
      return;
    }
    if (formData.password !== confirmPassword) {
      setPhase({
        phase: "form",
        invite: submittedInvite,
        submitError: t("auth.register.password_mismatch"),
      });
      return;
    }

    setPhase({ phase: "submitting", invite: submittedInvite });

    try {
      const data: RegisterResponse = await api.register(registerPayload);

      if (!("token" in data)) {
        const failureMessage = String(
          data.message || t("auth.register.failed"),
        );
        bugsnagClient.notify(new Error(failureMessage));
        setPhase({
          phase: "form",
          invite: submittedInvite,
          submitError: failureMessage,
        });
        return;
      }

      const organizationName =
        data.organization?.name || submittedInvite.orgName;
      const welcomeMessage = t("auth.register.welcome", {
        orgName: organizationName,
      });

      setPhase({
        phase: "success",
        invite: submittedInvite,
        successMessage: organizationName
          ? welcomeMessage
          : t("auth.register.success"),
      });

      trackEvent(ANALYTICS_EVENTS.USER_REGISTERED, {
        hasInvitation: Boolean(formData.invitationToken),
        organizationName: organizationName || undefined,
      });

      localStorage.setItem("token", data.token);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      if (organizationName) {
        sessionStorage.setItem("org_welcome", welcomeMessage);
      }

      setTimeout(() => {
        window.location.href = "/";
      }, REDIRECT_DELAY_MILLISECONDS);
    } catch (error) {
      bugsnagClient.notify(
        error instanceof Error ? error : new Error(String(error)),
      );
      setPhase({
        phase: "form",
        invite: submittedInvite,
        submitError: t("auth.request_failed"),
      });
    }
  };

  const getPageTitle = () => {
    if (phase.phase === "checking") {
      return t("auth.register.checking_title");
    }
    if (phase.phase === "invalid") {
      return t("auth.register.invalid_title");
    }
    if (phase.invite.orgName) {
      return t("auth.register.title", { orgName: phase.invite.orgName });
    }
    return t("auth.register.title_without_organization");
  };

  const getSubmitLabel = () => {
    if (phase.phase === "submitting") {
      return t("auth.register.submitting");
    }
    if (phase.phase === "success") {
      return t("auth.register.redirecting");
    }
    return t("auth.register.submit");
  };

  useEffect(() => {
    const queryToken = new URLSearchParams(location.search).get("token");
    const invitationToken = params.token || queryToken;

    if (!invitationToken) {
      setPhase({ phase: "invalid", reason: "invalidLink" });
      return;
    }

    const validateInvitation = async () => {
      try {
        const invitation: InviteValidationResponse =
          await api.validateInvite(invitationToken);

        if (!("isEmailRegistered" in invitation)) {
          setPhase({ phase: "invalid", reason: "invalidLink" });
          return;
        }
        if (invitation.isEmailRegistered) {
          navigate("/login", { replace: true });
          return;
        }

        setFormData((currentFormData) => ({
          ...currentFormData,
          email: invitation.email || "",
          invitationToken,
        }));
        setPhase({ phase: "form", invite: invitation, submitError: null });
      } catch (error) {
        setPhase({ phase: "invalid", reason: "validationFailed" });
        bugsnagClient.notify(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };

    validateInvitation();
  }, [location.search, navigate, params.token]);

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          src="/monco-logo-black.png"
          alt="Monco.ai"
          className="mx-auto h-12 w-auto"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {getPageTitle()}
        </h2>
        <div className="mt-4 flex justify-center">
          <LanguageSwitcher />
        </div>
      </div>
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <div
          className={`px-4 sm:px-10 ${
            phase.phase === "invalid"
              ? ""
              : "py-8 bg-white shadow sm:rounded-lg"
          }`}
        >
          {phase.phase === "checking" && (
            <div className="flex flex-col items-center py-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
              <p className="mt-4 text-sm text-gray-500">
                {t("auth.register.checking_description")}
              </p>
            </div>
          )}
          {phase.phase === "invalid" && (
            <>
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm text-center">
                {phase.reason === "validationFailed"
                  ? t("auth.register.validation_failed")
                  : t("auth.register.invalid_invitation")}{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="font-medium underline hover:text-red-800"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <p className="mt-6 text-center text-sm text-gray-600">
                {t("auth.register.already_have_account")}
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/login")}
                className="mt-3 w-full"
              >
                {t("auth.register.log_in")}
              </Button>
            </>
          )}
          {invite && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {phase.phase === "form" && phase.submitError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {phase.submitError}
                </div>
              )}

              {phase.phase === "success" && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                  {phase.successMessage}
                </div>
              )}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.register.first_name_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.register.last_name_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.register.email_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly={Boolean(invite.email)}
                    disabled={Boolean(invite.email)}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.register.password_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("auth.register.confirm_password_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={phase.phase !== "form"}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
                >
                  {getSubmitLabel()}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
